import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const privacySections = [
  {
    id: 'coleta',
    title: '1. Informações que Coletamos',
    content: [
      'Ao utilizar nosso site ou entrar em contato conosco, podemos coletar as seguintes informações:',
      '• Nome completo e nome da empresa',
      '• Endereço de e-mail e telefone',
      '• Mensagens enviadas pelo formulário de contato',
      '• Dados de navegação (páginas visitadas, tempo de permanência) via cookies analíticos',
      'Não coletamos dados de pagamento diretamente — todas as transações são realizadas fora do site.',
    ],
  },
  {
    id: 'uso',
    title: '2. Como Usamos suas Informações',
    content: [
      'As informações coletadas são utilizadas exclusivamente para:',
      '• Responder às suas solicitações de orçamento e dúvidas',
      '• Enviar informações sobre produtos e serviços solicitados',
      '• Melhorar a experiência de navegação no site',
      '• Cumprir obrigações legais e regulatórias',
      'Não vendemos, alugamos ou compartilhamos seus dados pessoais com terceiros para fins comerciais.',
    ],
  },
  {
    id: 'cookies',
    title: '3. Cookies',
    content: [
      'Nosso site pode utilizar cookies para melhorar sua experiência de navegação. Os cookies são pequenos arquivos de texto armazenados no seu dispositivo.',
      'Você pode configurar seu navegador para recusar cookies, mas isso pode afetar algumas funcionalidades do site.',
      'Utilizamos apenas cookies essenciais para o funcionamento do site e cookies analíticos para entender como os visitantes interagem com nossas páginas.',
    ],
  },
  {
    id: 'seguranca',
    title: '4. Segurança dos Dados',
    content: [
      'Adotamos medidas técnicas e organizacionais adequadas para proteger suas informações pessoais contra acesso não autorizado, alteração, divulgação ou destruição.',
      'Nosso site utiliza protocolo HTTPS para garantir a segurança das informações transmitidas.',
    ],
  },
  {
    id: 'direitos',
    title: '5. Seus Direitos (LGPD)',
    content: [
      'Em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018), você tem direito a:',
      '• Confirmar a existência de tratamento dos seus dados',
      '• Acessar seus dados pessoais',
      '• Corrigir dados incompletos, inexatos ou desatualizados',
      '• Solicitar a exclusão dos seus dados',
      '• Revogar o consentimento a qualquer momento',
      'Para exercer esses direitos, entre em contato pelo e-mail: ednilsonleocadio@gmail.com',
    ],
  },
  {
    id: 'contato-privacidade',
    title: '6. Contato sobre Privacidade',
    content: [
      'Se tiver dúvidas sobre esta Política de Privacidade ou sobre o tratamento dos seus dados, entre em contato:',
      '• E-mail: ednilsonleocadio@gmail.com',
      '• WhatsApp: (81) 98203-0285',
      '• Endereço: Av. Agamenon Magalhães, 1519, Maurício de Nassau, Caruaru - PE',
    ],
  },
];

const termsSections = [
  {
    id: 'uso-site',
    title: '1. Uso do Site',
    content: [
      'Ao acessar e utilizar o site da Home Office Premium, você concorda com estes Termos de Uso. O site é destinado exclusivamente para fins informativos e comerciais relacionados aos nossos produtos e serviços.',
      'É proibido utilizar o site para fins ilegais, fraudulentos ou que violem direitos de terceiros.',
    ],
  },
  {
    id: 'propriedade',
    title: '2. Propriedade Intelectual',
    content: [
      'Todo o conteúdo do site — incluindo textos, imagens, logotipos, marcas e design — é de propriedade da Home Office Premium ou de seus parceiros e está protegido por leis de propriedade intelectual.',
      'É proibida a reprodução, distribuição ou uso comercial de qualquer conteúdo sem autorização prévia e por escrito.',
    ],
  },
  {
    id: 'precos',
    title: '3. Preços e Disponibilidade',
    content: [
      'Os preços exibidos no site são meramente indicativos e podem ser alterados sem aviso prévio. Os preços definitivos são confirmados no momento do orçamento.',
      'A disponibilidade dos produtos está sujeita ao estoque e às condições de fornecimento dos fabricantes.',
    ],
  },
  {
    id: 'limitacao',
    title: '4. Limitação de Responsabilidade',
    content: [
      'A Home Office Premium não se responsabiliza por danos diretos ou indiretos decorrentes do uso ou impossibilidade de uso do site.',
      'Não garantimos que o site estará disponível de forma ininterrupta ou livre de erros.',
    ],
  },
  {
    id: 'alteracoes',
    title: '5. Alterações nos Termos',
    content: [
      'Reservamo-nos o direito de modificar estes Termos de Uso a qualquer momento. As alterações entram em vigor imediatamente após a publicação no site.',
      'O uso continuado do site após as alterações constitui aceitação dos novos termos.',
    ],
  },
];

export default function PrivacidadePage() {
  const [activeTab, setActiveTab] = useState<'privacidade' | 'termos'>('privacidade');

  useSEO({
    title: 'Política de Privacidade e Termos de Uso | Home Office Premium',
    description: 'Política de Privacidade e Termos de Uso da Home Office Premium. Saiba como tratamos seus dados pessoais em conformidade com a LGPD.',
    keywords: 'política privacidade Home Office Premium, LGPD, termos uso, proteção dados',
    path: '/privacidade',
    noindex: true,
  });

  useSchemaLD({
    id: 'schema-privacidade',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Política de Privacidade – Home Office Premium',
      description: 'Política de Privacidade e Termos de Uso da Home Office Premium em conformidade com a LGPD.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/privacidade`,
    },
  });

  const currentSections = activeTab === 'privacidade' ? privacySections : termsSections;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-20">
        <div className="bg-[#f8f8f6] py-16">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Legal</p>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Documentos Legais</h1>
            <p className="text-gray-500 text-base">Transparência e respeito aos seus direitos. Leia nossa política de privacidade e termos de uso.</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">
          {/* Tabs */}
          <div className="flex gap-2 bg-[#f8f8f6] rounded-full p-1 mb-10 w-fit">
            <button
              onClick={() => setActiveTab('privacidade')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${activeTab === 'privacidade' ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Política de Privacidade
            </button>
            <button
              onClick={() => setActiveTab('termos')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${activeTab === 'termos' ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Termos de Uso
            </button>
          </div>

          {/* Data de atualização */}
          <div className="flex items-center gap-2 mb-8 text-gray-400 text-xs">
            <i className="ri-calendar-line"></i>
            <span>Última atualização: 05 de abril de 2026</span>
          </div>

          {/* Conteúdo */}
          <div className="space-y-10">
            {currentSections.map(section => (
              <div key={section.id} id={section.id}>
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="w-1 h-5 bg-[#E8610A] rounded-full inline-block flex-shrink-0"></span>
                  {section.title}
                </h2>
                <div className="pl-4 space-y-2">
                  {section.content.map((line, i) => (
                    <p key={i} className={`text-sm leading-relaxed ${line.startsWith('•') ? 'text-gray-600 pl-2' : 'text-gray-600'}`}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* CTA de contato */}
          <div className="mt-16 bg-[#f8f8f6] rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-xl flex-shrink-0">
              <i className="ri-question-line text-[#E8610A] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-gray-900 font-semibold mb-1">Tem alguma dúvida?</h3>
              <p className="text-gray-500 text-sm">Entre em contato com nossa equipe. Respondemos em até 24 horas úteis.</p>
            </div>
            <Link
              to="/contato"
              className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              <i className="ri-mail-line"></i>
              Falar conosco
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}